import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from '@/components/ui/toast'
import { ConfirmDeleteDialog } from '@/components/shared'
import { useDeleteInvoice } from '@/hooks/useInvoices'
import { getApiErrorMessage } from '@/services/api'
import type { Invoice } from '@/types'

interface Props {
  invoice: Invoice | null
  onClose: () => void
  onDeleted?: () => void
  // When true, navigate to the invoice list after a successful delete.
  navigateOnDelete?: boolean
}

// Feedback #39: delete a Draft invoice with a confirmation guard. Backend
// rejects non-draft deletion; this dialog is only surfaced for draft rows.
// Figma 1336:31123.
export function DeleteInvoiceDialog({
  invoice,
  onClose,
  onDeleted,
  navigateOnDelete,
}: Props) {
  const { t } = useTranslation('invoices')
  const navigate = useNavigate()
  const del = useDeleteInvoice()

  const confirm = async () => {
    if (!invoice) return
    try {
      await del.mutateAsync(invoice.id)
      toast({ variant: 'success', title: t('deleteInvoice.success') })
      onDeleted?.()
      if (navigateOnDelete) navigate('/invoices')
    } catch (e) {
      toast({
        variant: 'destructive',
        title: getApiErrorMessage(e, t('deleteInvoice.error')),
      })
    }
  }

  return (
    <ConfirmDeleteDialog
      open={!!invoice}
      onOpenChange={o => !o && onClose()}
      title={t('deleteInvoice.title')}
      description={t('deleteInvoice.confirmText')}
      confirmLabel={t('deleteInvoice.confirm')}
      cancelLabel={t('deleteInvoice.cancel')}
      onConfirm={confirm}
      busy={del.isPending}
    />
  )
}

export default DeleteInvoiceDialog
